import { useModal } from '@ebay/nice-modal-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';

import {
  createZepetofulClient,
  fetchZepetofulContent,
  useZepApiClient,
  UseZepApiMutationOptions,
} from '@zep/apis';

import { Loading } from '../components/service/common/Loading';
import { IsoCode } from '../consts';

const baseUrl = '/v2/utils';

export const getZepetofulContent = async <T = object>(
  entryId: string,
  locale = 'en-US',
): Promise<T> => {
  const zepetofulClient = createZepetofulClient(locale);
  return fetchZepetofulContent(zepetofulClient, entryId, locale);
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useUploadImages = (
  type = 0,
  options?: UseZepApiMutationOptions<
    { status: number; urls: string[]; keys: string[] },
    FileList | File[]
  >,
) => {
  const client = useZepApiClient();
  const loadingSpinner = useModal(Loading);
  const { t } = useTranslation(['store']);

  return useMutation(
    ['upload-images', type],
    async (images: FileList | File[]) => {
      const formData = new FormData();
      Array.from(images).forEach(file => {
        formData.append('images', file);
      });
      formData.append('type', type.toString());

      const { data } = await client.post(`${baseUrl}/temp-images`, formData, {
        headers: { 'Content-type': 'multipart/form-data' },
      });

      return data;
    },
    {
      ...options,
      onMutate: variables => {
        options?.onMutate?.(variables);
        loadingSpinner.show(
          // @ts-ignore
          t('store:spinnerMessage.upload.image'),
        );
      },
      onSettled: (data, error, variables, context) => {
        options?.onSettled?.(data, error, variables, context);
        loadingSpinner.remove();
      },
    },
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export function useIsoCode(
  queryOptions?: Parameters<typeof useQuery<string>>[2],
) {
  const client = useZepApiClient();
  return useQuery<string>(
    [IsoCode],
    async () => {
      try {
        const { data } = await client.get('/v2/utils/iso-code');
        return data;
      } catch (error) {
        return 'KR';
      }
    },
    queryOptions,
  );
}
